import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton";
import BriaTooltip from "../../../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import RouterConstants from "../../../../../../../constants/RouterConstants";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { formatDate } from "../../../../../../../utils";
import iframeStore from "../../../../../../IframeNew/iframe-store.tsx";
import TgRequestAccessButton from "../../../../../Integrations/RequestAccess/TgRequestAccessButton.tsx";
import ModelAccessPopup from "../../../../../Models/Popups/ModelAccessPopup/ModelAccessPopup";
import { ViewTgModelLocationState } from "../../../../../Models/Viewer/TgModelViewer";
import { TgModel } from "../../../../../Models/store/tg-model-store";
import { isLegacyTgRoute } from "../../../../../utils/tg-utils.ts";
import TgModelActions from "../../Dashboard/Actions/TgModelActions.tsx";
import TgModelNameColumn from "../../Dashboard/Columns/Name/TgModelNameColumn.tsx";
import TgModelFeedbackCount from "../../Dashboard/FeedbackCount/TgModelFeedbackCount.tsx";
import styles from "./useModelTableHelpers.module.scss";

const isOptimisticModel = (model: TgModel): boolean => {
	try {
		const metadata = JSON.parse(model.custom_metadata);
		return metadata?.isOptimistic === true;
	} catch {
		return false;
	}
};

export const useModelTableHelpers = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.dashboard" });
	const { tgStore } = useAppStore();
	const { tgProjectStore, tgModelStore } = tgStore;
	const navigate = useNavigate();

	const openPlayground = (model: TgModel) => {
		const modelToViewState: ViewTgModelLocationState = {
			modelToView: model,
		};
		const fromLegacy = isLegacyTgRoute();
		const base = fromLegacy
			? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
			: RouterConstants.NEW_TG.PROJECTS.fullPath;
		navigate(
			`${base}/${model.project_id}/${RouterConstants.NEW_TG.MODELS.path}/${model.id}/${RouterConstants.NEW_TG.MODELS.EVALUATION.path}`,
			{
				state: { ...modelToViewState, fromLegacy },
			},
		);
	};

	const getPossibleAction = (
		model: TgModel,
		setLoadingRow: (
			loadingRow:
				| {
						data: TgModel;
						column: string;
				  }
				| undefined,
		) => void,
	) => {
		const handleAction = async (actionFunc: () => any) => {
			setLoadingRow({ data: model, column: "possible_action" });
			try {
				await actionFunc();
			} finally {
				setLoadingRow(undefined);
			}
		};

		switch (model.status) {
			case "Created":
				return (
					<BriaButton
						buttonType="secondaryMedium"
						onClick={() => handleAction(() => tgModelStore.startTraining(model))}
					>
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "InProgress":
				if (isOptimisticModel(model))
					return (
						<Box className={styles.loadingCenter}>
							<CircularProgress size={24} />
						</Box>
					);
				return (
					<BriaButton
						buttonType="primaryMedium"
						onClick={() => handleAction(() => tgModelStore.stopTraining(model.id))}
					>
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "Completed":
				return (
					<BriaButton buttonType="secondaryMedium" onClick={() => handleAction(() => openPlayground(model))}>
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "Stopping":
				return (
					<BriaButton disabled buttonType="secondaryMedium">
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "Stopped":
				return (
					<BriaButton
						buttonType="secondaryMedium"
						onClick={() => handleAction(() => tgModelStore.restartTraining(model))}
					>
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "Failed":
				return (
					<BriaButton
						buttonType="primaryMedium"
						onClick={() => handleAction(() => tgModelStore.startTraining(model))}
					>
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
			case "Deleted":
				return (
					<BriaButton disabled buttonType="secondaryMedium">
						{t(`table.possible_action.${model.status}`)}
					</BriaButton>
				);
		}
	};

	const getCreationBy = (model: TgModel) => {
		return (
			<Box>
				{(() => {
					try {
						const trained_by = JSON.parse(model.custom_metadata)?.user_created_the_model?.name;
						return (
							trained_by || (
								<BriaTooltip title={t("trained_by_not_found")}>
									<Box component="span">-</Box>
								</BriaTooltip>
							)
						);
					} catch {
						return (
							<BriaTooltip title={t("trained_by_not_found")}>
								<Box component="span">-</Box>
							</BriaTooltip>
						);
					}
				})()}
				<br />
				<Box component="span" style={{ fontSize: "12px", color: "#666" }}>
					{formatDate(model.created_at)}
				</Box>
			</Box>
		);
	};

	const getIntegrateNowButton = (model: TgModel) => {
		return !iframeStore.isIframe() && model.status === "Completed" ? (
			<TgRequestAccessButton inheritChildButtonStyle buttonClassName={styles.integrateNowRequestAccessButton}>
				<ModelAccessPopup
					buttonType={"primaryMedium"}
					model={model}
					project={tgProjectStore.projectToView}
					buttonText={t("integrateButton")}
				/>
			</TgRequestAccessButton>
		) : (
			<Box />
		);
	};

	const getNameColumn = (model: TgModel) => {
		return <TgModelNameColumn {...{ model }} />;
	};

	const getStatusColumn = (model: TgModel) => {
		return (
			<>
				{model.status === "Failed" ? (
					<BriaTooltip title={t("table.failedTooltip")}>
						<Box className={styles.failedStatus}>{t(`table.status.${model.status}`)}</Box>
					</BriaTooltip>
				) : (
					<Box>{t(`table.status.${model.status}`)}</Box>
				)}
			</>
		);
	};

	const getActionsColumn = (model: TgModel) => {
		const dataset = tgProjectStore.paginatedDatasets.items.find((d) => d.id === model?.dataset_id);
		return <TgModelActions {...{ model }} dataset={dataset} />;
	};

	const getFeedbackCountCell = (model: TgModel) => {
		return <TgModelFeedbackCount model={model} />;
	};

	return {
		getPossibleAction,
		getCreationBy,
		getIntegrateNowButton,
		getNameColumn,
		getStatusColumn,
		getActionsColumn,
		getFeedbackCountCell,
	};
};
